import { useCallback, useEffect, useState } from 'react';
import { NWClient } from '../../../client/NWClient';
import { AITemplateModel } from '../../../models/ai.template.model';
import { toast } from 'react-toastify';
import { DocumentTemplateModel } from '../../../models/document.template.model';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { ROUTES } from '../../../common/constants/routes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { showMessage } from '../../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import { Document } from '../../document-vault/components/document-list-vault/types';
import { TAGS_LABELS } from '../../../common/constants/tags-labels';
import { Helpers } from '../../../common/helpers/helpers';
import {
  addPlaceholderId,
  clearPlaceholdersIds,
  removePlaceholderId,
  selectTemplatePlaceholdersIds,
} from '../../../slices/templatePlaceholdersIds/templatePlaceholdersIds';
import {
  addPlaceholderIndex,
  clearPlaceholdersIndexes,
  removePlaceholderIndex,
} from '../../../slices/templatePlaceholdersIndexes/templatePlaceholdersIndexes';

export const useTemplateCreation = (
  id: number,
  isGenerated: boolean,
  documentUploadHTML?: number,
  documentName?: string,
  documentDetails?: Document,
  content?: string
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();
  const generateTemplate = params.get('generateTemplate');
  const currentUser = useAppSelector(selectUser);
  /*const chosenPlaceholdersIds: number[] = JSON.parse(
    localStorage.getItem('templatePlaceholdersIds')
  );*/
  const chosenPlaceholdersIds = useAppSelector(selectTemplatePlaceholdersIds);
  const [templateCreationEnabled, setTemplateCreationEnabled] = useState<boolean>(false);
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [documentTemplate, setDocumentTemplate] = useState<DocumentTemplateModel>(null);
  const [templateCreationStep, setTemplateCreationStep] = useState<number>(1);
  const [deletePlaceholdersMessageVisible, setDeletePlaceholdersMessageVisible] =
    useState<boolean>(false);
  const [template, setTemplate] = useState<
    {
      id?: number;
      data: { field: string; value: string; placeholder: string };
    }[]
  >([]);
  const [templateName, setTemplateName] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [documentTypeLabel, setDocumentTypeLabel] = useState<string>('');
  const [documentWorkflowLabel, setDocumentWorkflowLabel] = useState<string>('');
  const [customLabel, setCustomLabel] = useState<string>('');

  const categoryArray = ['Business', 'Customers', 'Employment', 'Suppliers'];

  const categorySelectValues = categoryArray.map((category) => ({
    value: category,
    label: category,
  }));

  const documentWorkflowLabelsEmployment = TAGS_LABELS.documentWorkflow.employment.map((el) =>
    t(el)
  );
  const documentWorkflowLabelsEmploymentSorted = documentWorkflowLabelsEmployment.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsSupplier = TAGS_LABELS.documentWorkflow.supplier.map((el) => t(el));
  const documentWorkflowLabelsSupplierSorted = documentWorkflowLabelsSupplier.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsCustomer = TAGS_LABELS.documentWorkflow.customer.map((el) => t(el));
  const documentWorkflowLabelsCustomerSorted = documentWorkflowLabelsCustomer.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsBusiness = TAGS_LABELS.documentWorkflow.business.map((el) => t(el));
  const documentWorkflowLabelsBusinessSorted = documentWorkflowLabelsBusiness.sort((a, b) =>
    a.localeCompare(b)
  );

  const workflowsArray = () => {
    // Use the current category state rather than documentDetails.category
    const currentCategory = category ? category.toLowerCase() : '';

    switch (currentCategory) {
      case 'employment':
        return documentWorkflowLabelsEmploymentSorted.map((el) => ({ label: el, value: el }));
      case 'suppliers':
        return documentWorkflowLabelsSupplierSorted.map((el) => ({ label: el, value: el }));
      case 'customers':
        return documentWorkflowLabelsCustomerSorted.map((el) => ({ label: el, value: el }));
      case 'business':
        return documentWorkflowLabelsBusinessSorted.map((el) => ({ label: el, value: el }));
      default:
        return [];
    }
  };

  const placeholderCheckUncheck = (event: any, index: number, id: number) => {
    if (event.target.checked) {
      dispatch(addPlaceholderId(id));
      dispatch(addPlaceholderIndex(index));
    } else {
      dispatch(removePlaceholderId(id));
      dispatch(removePlaceholderIndex(index));
    }
  };

  const documentTypeLabels = TAGS_LABELS.documentType.map((el) => t(el));
  const getTypeLabels = () => {
    return documentTypeLabels.map((el) => ({ label: el, value: el }));
  };

  const requestData = useCallback(() => {
    if (isGenerated) {
      return { document_contract_id: id };
    } else {
      if (localStorage.getItem('doc_latest_version')) {
        return { document_upload_id: id, document_upload_html: content };
      } else {
        return { document_upload_id: id };
      }
    }
  }, [content, id, isGenerated]);
  const creatingTemplateInitial = useCallback(() => {
    setPendingStatus(true);
    NWClient.post('ai-template', requestData())
      .then((res: AITemplateModel[]) => {
        localStorage.setItem('AITemplateFail', 'false');
        setTemplate(
          res.map((el) => ({
            id: el.id,
            data: { ...el.data },
          }))
        );
      })
      .catch((error) => {
        setTemplateCreationEnabled(false);
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else {
          if (localStorage.getItem('AITemplateFail') === 'true') {
            toast.error(error.message ? error.message : t('messages.errorOccurred'));
          } else {
            localStorage.setItem('AITemplateFail', 'true');
            toast.error(t('messages.firstAITemplateFail'));
          }
        }
      })
      .finally(() => {
        setPendingStatus(false);
        localStorage.setItem('templatePlaceholdersIndexes', JSON.stringify([]));
        localStorage.setItem('templatePlaceholdersIds', JSON.stringify([]));
      });
  }, [dispatch, requestData, t]);
  const creatingTemplate = () => {
    setPendingStatus(true);
    NWClient.post('document-template', {
      document_upload: id,
      user: currentUser.id,
      content,
      name: templateName
        ? templateName
        : documentName?.endsWith('.pdf') ||
            documentName?.endsWith('.docx') ||
            documentName?.endsWith('.doc')
          ? documentName?.replace('.pdf', '').replace('.docx', '').replace('.doc', '')
          : documentName,
      category: category || documentDetails?.category,
      dt_workflows: documentWorkflowLabel
        ? [documentWorkflowLabel]
        : documentDetails && 'du_workflows' in documentDetails
          ? documentDetails?.du_workflows
          : documentDetails?.dc_workflows,
      dt_types: documentTypeLabel
        ? [documentTypeLabel]
        : documentDetails && 'du_types' in documentDetails
          ? documentDetails?.du_types
          : documentDetails?.dc_types,
      dt_label: customLabel
        ? [customLabel]
        : documentDetails && 'du_label' in documentDetails
          ? documentDetails?.du_label
          : documentDetails?.dc_label,
    })
      .then((res: DocumentTemplateModel) => {
        toast.success(t('messages.templateCreated'));
        setDocumentTemplate(res);
        navigatingToCreatedTemplate(res.id);
      })
      .catch((error) => {
        setPendingStatus(false);
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else {
          toast.error(error.message ? error.message : t('messages.errorOccurred'));
        }
      });
  };
  const navigatingToCreatedTemplate = (id: number) => {
    navigate(`${ROUTES.DOCUMENT_LIBRARY}/company-templates/${id}?templateViewEditMode=true`);
  };

  const deletePlaceholders = () => {
    if (chosenPlaceholdersIds.length > 0) {
      setPendingStatus(true);
      NWClient.post('document-template-placeholder/clear', {
        user: currentUser.id,
        placeholder_ids: chosenPlaceholdersIds,
      })
        .then(() => {
          toast.success(t('messages.templatePlaceholdersDeleted'));
          //to refactor in future - when we enable placeholders adding
          // MEHDI COMMENT - This call causes deleted placeholders to reappear. Will comment out
          // MEHDI COMMENT - For placeholders adding, we won't need to call AI-template but we will add a dedicated endpoint in the future
          // creatingTemplateInitial();
          setTemplate((prevTemplate) =>
            prevTemplate.filter((entry) => !chosenPlaceholdersIds.includes(entry.id))
          );
          dispatch(clearPlaceholdersIds());
          dispatch(clearPlaceholdersIndexes());
          setPendingStatus(false);
        })
        .catch((error) => {
          toast.error(error.message ? error.message : t('messages.errorOccurred'));
          setPendingStatus(false);
        });
    }
  };
  const addTemplatePlaceholderRow = () => {
    const templateCopy = template.slice();
    const item = { data: { field: '', value: '', placeholder: '' } };
    templateCopy.push(item);
    setTemplate(templateCopy);
  };

  const handleTemplateCreation = useCallback(() => {
    if (templateCreationEnabled && id && documentUploadHTML) {
      creatingTemplateInitial();
    }
  }, [templateCreationEnabled, id, documentUploadHTML, creatingTemplateInitial]);

  useEffect(() => {
    if (generateTemplate) {
      setTemplateCreationEnabled(true);
    }
  }, [generateTemplate]);

  useEffect(() => {
    handleTemplateCreation();
  }, [handleTemplateCreation]);

  useEffect(() => {
    if (documentDetails) {
      const labelProperty = 'du_label' in documentDetails ? 'du_label' : 'dc_label';
      const workflowProperty = 'du_workflows' in documentDetails ? 'du_workflows' : 'dc_workflows';
      const typeProperty = 'du_types' in documentDetails ? 'du_types' : 'dc_types';
      setTemplateName(documentDetails?.name);
      setCategory(
        documentDetails?.category ? Helpers.capitalizeFirstLetter(documentDetails?.category) : ''
      );
      setCustomLabel(
        documentDetails[labelProperty] && documentDetails[labelProperty].length > 0
          ? documentDetails[labelProperty][0]
          : ''
      );
      setDocumentWorkflowLabel(
        documentDetails[workflowProperty] && documentDetails[workflowProperty].length > 0
          ? documentDetails[workflowProperty][0]
          : ''
      );
      setDocumentTypeLabel(
        documentDetails[typeProperty] && documentDetails[typeProperty].length > 0
          ? documentDetails[typeProperty][0]
          : ''
      );
    }
    return () => {
      localStorage.setItem('templatePlaceholdersIndexes', JSON.stringify([]));
      localStorage.setItem('templatePlaceholdersIds', JSON.stringify([]));
    };
  }, [documentDetails]);

  return {
    templateCreationEnabled,
    setTemplateCreationEnabled,
    pendingStatus,
    setPendingStatus,
    template,
    setTemplate,
    creatingTemplate,
    documentTemplate,
    setDocumentTemplate,
    navigatingToCreatedTemplate,
    deletePlaceholders,
    addTemplatePlaceholderRow,
    templateCreationStep,
    setTemplateCreationStep,
    deletePlaceholdersMessageVisible,
    setDeletePlaceholdersMessageVisible,
    templateName,
    setTemplateName,
    category,
    setCategory,
    documentTypeLabel,
    setDocumentTypeLabel,
    documentWorkflowLabel,
    setDocumentWorkflowLabel,
    customLabel,
    setCustomLabel,
    categorySelectValues,
    getTypeLabels,
    workflowsArray,
    categoryArray,
    placeholderCheckUncheck,
  };
};
